import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CalendarClock, CalendarDays, FileText, Loader2, Package, Pencil, Receipt, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, getApiErrorMessage } from "../../api/client";
import { unwrap, unwrapArray } from "../../lib/api-envelope";
import { fmtCurrency, parseMoney } from "../../lib/display-labels";
import { formatCalendarDate, formatInstant, formatInstantDay } from "../../lib/calendar-date";
import { useAuth } from "../../contexts/AuthContext";
import RentalOrderEditDialog from "../../components/rentals/RentalOrderEditDialog";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";
import { PageHeaderBand } from "../../components/layout/PageHeaderBand";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import { displayValue } from "../../features/fieldiq-handoff/contracts";
import { DhEmpty, DhPanel, DhSpec } from "../downhole/DownholeBits";
import RentalBillingPreview from "../../components/rentals/RentalBillingPreview";
import PortalLinks, { portalLinksQueryKey } from "../../components/rentals/PortalLinks";
import { DAMAGE_CHARGE_READ_ROLES, DAMAGE_CHARGE_WRITE_ROLES, DamageChargeRows } from "../../components/rentals/DamageCharges";
import type { RentalDamageCharge } from "../../types/rental-damage-charge";
import { missingRateNote } from "../../lib/missing-rate-note";

/**
 * One rental order, /rentals/orders/:id (#815 PR D). It was the right-hand
 * panel of Active Rentals' split pane; Active Rentals is now a full-width
 * register and a row opens this page.
 *
 * Band: the order number, Assets / On rent since / Expected return / Invoices,
 * and the actions (Generate invoice, Close order). Body: the order record, its
 * assets, what an invoice would charge now (RentalBillingPreview, the API's
 * per-line figures), and its invoices.
 *
 * GENERATE (api#958): an invoice bills only what is NEW since the last
 * non-void invoice. Nothing new is the API's 422 `nothing_to_bill`; an
 * unpriced asset is a 422 naming it. Both are shown as sent, in the dialog.
 * A void invoice cannot be revived — voiding released its days and fees — so
 * when the newest invoice is void, one line says Generate bills them again.
 *
 * EDIT (#815 PR F): RentalOrderEditDialog, PUT /orders/:id with clear-to-null.
 * Offered to the roles the API lets write an order (JOB_WRITE_ROLES). Each
 * dispatch on the order opens /rentals/dispatches/:id.
 */

const str = (v: unknown): string | null => (v === null || v === undefined || v === "" ? null : String(v));
const day = (d: unknown) => formatCalendarDate(str(d), { month: "short", day: "numeric", year: "numeric" }) ?? "—";
const BAND_SECONDARY =
  "inline-flex h-10 items-center gap-1.5 rounded-fi-control border border-white/25 bg-fi-brand px-4 text-sm font-bold transition hover:bg-white/10 disabled:opacity-50";
const PRIMARY = "fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50";

/** Exhaustive over `rental_invoices.status` as the API writes it; anything else is a dash. */
const INVOICE_STATUS: Record<string, string> = { draft: "Draft", sent: "Sent", paid: "Paid", overdue: "Overdue", void: "Void" };
/**
 * The order's status as the API reads it today: `effective_status` (api#1091),
 * which is `reserved` for an active order whose start date has not come, and
 * the stored `status` otherwise. Anything else is a dash.
 */
const ORDER_STATUS: Record<string, string> = { draft: "Draft", reserved: "Reserved", active: "Active", closed: "Closed", cancelled: "Cancelled" };
/** RENTAL_ORDER_WRITE on the API: requireRole(...JOB_WRITE_ROLES). */
const ORDER_WRITE_ROLES = ["admin", "office_staff", "supervisor", "dispatcher"];
const DISPATCH_TYPE: Record<string, string> = { delivery: "Delivery", pickup: "Pickup", transfer: "Transfer", swap: "Swap", relocation: "Relocation" };
const DISPATCH_STATUS: Record<string, string> = {
  pending: "Pending", scheduled: "Scheduled", in_transit: "In transit", completed: "Completed", delivered: "Delivered", returned: "Returned", cancelled: "Cancelled",
};

export default function RentalOrderDetail() {
  const navigate = useNavigate();
  const { id = "" } = useParams<{ id: string }>();
  const timeZone = useTenantTimeZone();
  const queryClient = useQueryClient();
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceSubmitting, setInvoiceSubmitting] = useState(false);
  const [invoiceRefusal, setInvoiceRefusal] = useState<string | null>(null);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeSubmitting, setCloseSubmitting] = useState(false);
  // Cancel (fe#815 cancel): a reason is required, and the API decides whether
  // the order can be cancelled — its refusal (ORDER_NOT_CANCELLABLE: equipment
  // has gone out, an invoice exists) is shown in the dialog as sent.
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelRefusal, setCancelRefusal] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkEmail, setLinkEmail] = useState("");
  const [linkSubmitting, setLinkSubmitting] = useState(false);
  const [linkRefusal, setLinkRefusal] = useState<string | null>(null);
  const { user, company } = useAuth();
  const role = String((user as { role?: string } | null)?.role ?? "");
  const canWrite = ORDER_WRITE_ROLES.includes(role);
  // Damage charges (#815 G): the billing roles read them; admin and office staff price or waive.
  const canReadCharges = DAMAGE_CHARGE_READ_ROLES.includes(role);
  const canWriteCharges = DAMAGE_CHARGE_WRITE_ROLES.includes(role);
  const chargesQuery = useQuery({
    queryKey: ["rental-damage-charges", "order", id],
    queryFn: async () => unwrapArray<RentalDamageCharge>((await api.rentals.damageCharges.forOrder(id)).data),
    enabled: !!id && canReadCharges,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const chargesFailed = queryLoadFailure(chargesQuery) != null;
  const charges = chargesQuery.data ?? [];

  const orderQuery = useQuery({
    queryKey: ["rentals-order", id],
    queryFn: async () => {
      const raw = unwrap<Record<string, unknown>>((await api.rentals.orders.get(id)).data);
      return raw && raw.id ? raw : null;
    },
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const billingQuery = useQuery({
    queryKey: ["rentals-billing", id],
    queryFn: () => api.rentals.billing.calculate(id).then((r) => unwrap<Record<string, unknown>>(r.data) ?? {}),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(orderQuery);
  const order = orderQuery.data ?? null;
  const loading = orderQuery.isLoading && !failure;
  const assets = unwrapArray<Record<string, unknown>>(order?.assets);
  const invoices = unwrapArray<Record<string, unknown>>(order?.invoices);
  const dispatches = unwrapArray<Record<string, unknown>>(order?.dispatches);
  // GET /orders/:id sends invoices newest first.
  const newest = invoices[0];
  const newestVoid = newest && newest.status === "void";
  const billingFailed = queryLoadFailure(billingQuery) != null;
  const billing = billingFailed ? undefined : billingQuery.data;
  // The preview's total is null while an asset has no rate (api#854).
  const unratedNote = billing && parseMoney(billing.total) === null ? missingRateNote(billing.unrated_count, { one: "asset", many: "assets" }) : null;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["rentals-order", id] });
    queryClient.invalidateQueries({ queryKey: ["rentals-billing", id] });
    queryClient.invalidateQueries({ queryKey: ["rentals-orders"] });
  };

  const handleGenerate = async () => {
    setInvoiceSubmitting(true);
    setInvoiceRefusal(null);
    try {
      const res = await api.rentals.invoices.generateFromOrder(id);
      const inv = unwrap<{ invoice?: { invoice_number?: string } }>(res.data)?.invoice;
      setInvoiceOpen(false);
      toast.success(inv?.invoice_number ? `Invoice ${inv.invoice_number} generated` : "Invoice generated");
      refresh();
    } catch (err) {
      // A 422 is the API refusing, in its own words, and writing nothing:
      // nothing new to bill since the last invoice (`nothing_to_bill`), or an
      // asset with no billing rate, named. A 409 is the order not started yet
      // (ORDER_NOT_STARTED): it became reserved while this page was open. The
      // dialog stays open and says so.
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 422 || status === 409) setInvoiceRefusal(getApiErrorMessage(err, "This order cannot be invoiced yet."));
      else toast.error(getApiErrorMessage(err, "Failed to generate invoice"));
    } finally {
      setInvoiceSubmitting(false);
    }
  };

  const handleClose = async () => {
    setCloseSubmitting(true);
    try {
      await api.rentals.orders.close(id);
      setCloseOpen(false);
      toast.success("Order closed");
      refresh();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to close order"));
    } finally {
      setCloseSubmitting(false);
    }
  };

  const handleCancel = async () => {
    setCancelSubmitting(true);
    setCancelRefusal(null);
    try {
      await api.rentals.orders.cancel(id, cancelReason.trim());
      setCancelOpen(false);
      setCancelReason("");
      toast.success("Order cancelled");
      refresh();
    } catch (err) {
      setCancelRefusal(getApiErrorMessage(err, "The order was not cancelled."));
    } finally {
      setCancelSubmitting(false);
    }
  };

  // The prefill is the order's site contact, the one address this payload
  // carries. The office confirms or replaces it; the API sends only to what is
  // in the box, and refuses (409) an order whose link would not open.
  const openSendLink = () => {
    setLinkRefusal(null);
    setLinkEmail(str(order?.contact_email) ?? "");
    setLinkOpen(true);
  };

  const handleSendLink = async () => {
    setLinkSubmitting(true);
    setLinkRefusal(null);
    try {
      const res = await api.rentals.orders.sendPortalLink(id, linkEmail.trim());
      const sentTo = unwrap<{ sentTo?: string }>(res.data)?.sentTo ?? linkEmail.trim();
      setLinkOpen(false);
      toast.success(`Portal link sent to ${sentTo}`);
      void queryClient.invalidateQueries({ queryKey: portalLinksQueryKey(id) });
    } catch (err) {
      // Every refusal stays in the dialog: a bad address, a dead order (409) or
      // a provider that did not take the email (502, the old link still works).
      setLinkRefusal(getApiErrorMessage(err, "The portal link was not sent."));
    } finally {
      setLinkSubmitting(false);
    }
  };

  /** A cancelled order is final: the API refuses every write to it (409 ORDER_CANCELLED). */
  const cancelled = order?.status === "cancelled";
  const open = order && order.status !== "closed" && !cancelled;
  /** Committed but not started (api#1091): nothing has gone on rent, so there is nothing to bill. */
  const reserved = order?.effective_status === "reserved";
  const actions =
    order && (open || (canWrite && !cancelled)) ? (
      <div className="flex flex-wrap items-center gap-2">
        {canWrite && !cancelled && (
          <button type="button" className={BAND_SECONDARY} onClick={() => setEditOpen(true)}>
            <Pencil className="size-4" aria-hidden="true" />
            Edit order
          </button>
        )}
        {canWrite && !cancelled && (
          <button type="button" className={BAND_SECONDARY} onClick={openSendLink}>
            <Send className="size-4" aria-hidden="true" />
            Send portal link
          </button>
        )}
        {open && (
          <>
            <button type="button" className={BAND_SECONDARY} onClick={() => setCloseOpen(true)}>
              Close order
            </button>
            {canWrite && (
              <button
                type="button"
                className={BAND_SECONDARY}
                onClick={() => { setCancelRefusal(null); setCancelReason(""); setCancelOpen(true); }}
              >
                Cancel order
              </button>
            )}
            {/* Not on a reserved order (operator, 2026-10-15): the API refuses it too
              * (409 ORDER_NOT_STARTED), and the button would offer work that cannot happen. */}
            {!reserved && (
              <button type="button" className={PRIMARY} onClick={() => { setInvoiceRefusal(null); setInvoiceOpen(true); }}>
                <Receipt className="size-4" aria-hidden="true" />
                Generate invoice
              </button>
            )}
          </>
        )}
      </div>
    ) : undefined;

  return (
    <div data-testid="rental-order-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Surface rentals · Rental order"
        title={order ? str(order.order_number) ?? "—" : loading ? "Loading…" : "Rental order"}
        onBack={() => navigate("/rentals/active")}
        backLabel="Back to Rental Orders"
        actions={actions}
        stats={
          !order
            ? undefined
            : [
                { icon: <Package className="size-4" aria-hidden="true" />, label: "Assets", sub: "On this order", tone: "neutral", value: displayValue(assets.length) },
                // A reserved order (api#1091) is not on rent yet: the same date is when it starts.
                order.effective_status === "reserved"
                  ? { icon: <CalendarDays className="size-4" aria-hidden="true" />, label: "Starts", sub: "Reserved, not on rent", tone: "info", value: day(order.start_date) }
                  : { icon: <CalendarDays className="size-4" aria-hidden="true" />, label: "On rent since", sub: "Start date", tone: "info", value: day(order.start_date) },
                { icon: <CalendarClock className="size-4" aria-hidden="true" />, label: "Expected return", sub: "Due back", tone: "accent", value: order.expected_return ? day(order.expected_return) : "Open-ended" },
                { icon: <FileText className="size-4" aria-hidden="true" />, label: "Invoices", sub: "On this order", tone: "success", value: displayValue(invoices.length) },
              ]
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading order" />
            </div>
          ) : !order ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              {failure ? (
                <LoadFailureNotice error={queryFailureError(orderQuery)} kind={failure} subject="this order" />
              ) : (
                <DhEmpty>This order does not exist, or it was deleted.</DhEmpty>
              )}
            </div>
          ) : (
            <>
              <DhPanel eyebrow="Order" title="Order record" testId="rental-order-record">
                <dl className="grid grid-cols-1 gap-x-6 gap-y-4 p-4 sm:grid-cols-2 md:grid-cols-4">
                  <DhSpec label="Customer" value={str(order.customer_name) ?? "—"} />
                  <DhSpec label="Status" value={ORDER_STATUS[String(order.effective_status ?? order.status)] ?? "—"} />
                  {cancelled && (
                    <DhSpec
                      label="Cancelled"
                      value={[
                        order.cancelled_at ? formatInstantDay(String(order.cancelled_at), timeZone) : null,
                        str(order.cancel_reason) ?? "no reason on file",
                      ].filter(Boolean).join(" · ")}
                    />
                  )}
                  <DhSpec label="Well / site" value={str(order.well_name) ?? "—"} />
                  <DhSpec label="LSD" value={str(order.lsd) ?? "—"} mono />
                  <DhSpec label="AFE" value={str(order.afe_number) ?? "—"} />
                  <DhSpec label="PO" value={str(order.po_number) ?? "—"} />
                  <DhSpec label={reserved ? "Starts" : "Started"} value={day(order.start_date)} />
                  <DhSpec label="Expected return" value={order.expected_return ? day(order.expected_return) : "Open-ended"} />
                  <DhSpec label="Job site" value={[str(order.job_site_name), str(order.job_site_address)].filter(Boolean).join(" · ") || "—"} />
                  <DhSpec label="Site contact" value={[str(order.contact_name), str(order.contact_phone), str(order.contact_email)].filter(Boolean).join(" · ") || "—"} />
                  <DhSpec label="Province / state" value={str(order.province_state) ?? "—"} />
                </dl>
                {str(order.notes) && (
                  <p data-testid="order-notes" className="whitespace-pre-line border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
                    <span className="block text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Notes</span>
                    {str(order.notes)}
                  </p>
                )}
              </DhPanel>

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <DhPanel eyebrow="Equipment" title="Assets on this order" testId="rental-order-assets">
                  {assets.length === 0 ? (
                    <DhEmpty>None on this order.</DhEmpty>
                  ) : (
                    <ul className="divide-y divide-fieldiq-border">
                      {assets.map((a, i) => (
                        <li key={String(a.id ?? i)} className="flex justify-between gap-3 px-4 py-3 text-sm">
                          <span className="text-fieldiq-text">{str(a.asset_name) ?? "—"}</span>
                          <span className="font-mono text-xs text-fieldiq-muted">{str(a.asset_number) ?? "—"}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </DhPanel>

                <DhPanel eyebrow="Billing" title="What an invoice would charge now" testId="billing-preview">
                  <div className="p-4">
                    {billingFailed ? (
                      <LoadFailureNotice error={queryFailureError(billingQuery)} subject="the billing preview" allowed="admins, office staff, supervisors and dispatchers" />
                    ) : billingQuery.isLoading || !billing ? (
                      <div className="h-16 animate-pulse rounded-fi-control bg-fieldiq-dark/60" aria-hidden="true" />
                    ) : (
                      <RentalBillingPreview billing={billing} timeZone={timeZone} />
                    )}
                  </div>
                </DhPanel>
              </div>

              <DhPanel eyebrow="Logistics" title="Dispatches" testId="rental-order-dispatches">
                {dispatches.length === 0 ? (
                  <DhEmpty>None yet.</DhEmpty>
                ) : (
                  <ul className="divide-y divide-fieldiq-border">
                    {dispatches.map((d, i) => (
                      <li key={String(d.id ?? i)} data-testid="order-dispatch" className="flex flex-wrap justify-between gap-3 px-4 py-3 text-sm">
                        {str(d.id) ? (
                          <Link className="text-fieldiq-text underline underline-offset-2" to={`/rentals/dispatches/${encodeURIComponent(String(d.id))}`}>
                            {DISPATCH_TYPE[String(d.dispatch_type)] ?? "Dispatch"}
                          </Link>
                        ) : (
                          <span className="text-fieldiq-text">{DISPATCH_TYPE[String(d.dispatch_type)] ?? "Dispatch"}</span>
                        )}
                        <span className="text-fieldiq-muted">
                          {DISPATCH_STATUS[String(d.status)] ?? "—"} · {formatInstant(str(d.scheduled_date), timeZone) ?? "Not scheduled"}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </DhPanel>

              {canWrite && <PortalLinks orderId={id} timeZone={timeZone} />}

              {canReadCharges && (
                <DhPanel eyebrow="Billing" title="Damage charges" testId="rental-order-damage-charges">
                  {chargesFailed ? (
                    <LoadFailureNotice error={queryFailureError(chargesQuery)} subject="damage charges" />
                  ) : chargesQuery.isLoading ? (
                    <div className="p-4" aria-hidden="true"><div className="h-12 animate-pulse rounded-fi-control bg-fieldiq-dark/60" /></div>
                  ) : charges.length === 0 ? (
                    <DhEmpty>None. A return that finds damage adds one here.</DhEmpty>
                  ) : (
                    <DamageChargeRows charges={charges} canWrite={canWriteCharges} timeZone={timeZone} />
                  )}
                </DhPanel>
              )}

              <DhPanel eyebrow="Billing" title="Invoices" testId="rental-order-invoices">
                {invoices.length === 0 ? (
                  <DhEmpty>None yet.</DhEmpty>
                ) : (
                  <ul className="divide-y divide-fieldiq-border">
                    {invoices.map((inv, i) => (
                      <li key={String(inv.id ?? i)} data-testid="order-invoice" className="flex flex-wrap justify-between gap-3 px-4 py-3 text-sm">
                        {str(inv.id) ? (
                          <Link className="font-mono text-fieldiq-text underline underline-offset-2" to={`/rentals/invoices/${encodeURIComponent(String(inv.id))}`}>
                            {str(inv.invoice_number) ?? "—"}
                          </Link>
                        ) : (
                          <span className="font-mono text-fieldiq-text">{str(inv.invoice_number) ?? "—"}</span>
                        )}
                        <span className="text-fieldiq-muted">
                          {INVOICE_STATUS[String(inv.status)] ?? "—"} · {day(inv.invoice_date)} · {fmtCurrency(parseMoney(inv.total))}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {newestVoid && order.status !== "closed" && (
                  <p data-testid="void-regenerate-note" className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-text">
                    {str(newest.invoice_number) ?? "The last invoice"} is void; Generate invoice bills its days and fees again.
                  </p>
                )}
              </DhPanel>
            </>
          )}
        </div>
      </section>

      <Dialog open={invoiceOpen} onOpenChange={(open) => { if (!open) { setInvoiceOpen(false); setInvoiceRefusal(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Invoice</DialogTitle>
          </DialogHeader>
          <dl className="space-y-2 rounded-fi-control border border-fieldiq-border p-4 text-sm" data-testid="invoice-confirm">
            <div className="flex justify-between gap-3"><dt className="text-fieldiq-muted">Customer</dt><dd className="font-semibold text-fieldiq-text">{str(order?.customer_name) ?? "—"}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-fieldiq-muted">Order</dt><dd className="font-mono text-fieldiq-text">{str(order?.order_number) ?? "—"}</dd></div>
            <div className="flex justify-between gap-3 border-t border-fieldiq-border pt-2 font-bold">
              <dt className="text-fieldiq-text">Total, as billed now</dt>
              <dd className="text-fieldiq-text">{billingFailed || billingQuery.isLoading ? "—" : fmtCurrency(parseMoney(billing?.total))}</dd>
            </div>
          </dl>
          {unratedNote && (
            <p data-testid="invoice-unrated-note" className="text-xs text-fieldiq-text">Not totalled: {unratedNote}.</p>
          )}
          {invoiceRefusal && (
            <p role="alert" data-testid="invoice-refusal" className="rounded-fi-control border border-fieldiq-border px-3 py-2 text-xs text-fieldiq-text">
              {invoiceRefusal}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setInvoiceOpen(false); setInvoiceRefusal(null); }} disabled={invoiceSubmitting}>Cancel</Button>
            <Button onClick={handleGenerate} disabled={invoiceSubmitting}>
              {invoiceSubmitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Confirm &amp; Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {editOpen && order && (
        <RentalOrderEditDialog order={order} country={company?.country === "US" ? "US" : "CA"} onClose={() => setEditOpen(false)} onSaved={refresh} />
      )}

      {/* POST /orders/:id/close takes no body: it ends every open rental period
          now and marks every asset on the order Available. */}
      <Dialog open={closeOpen} onOpenChange={(open) => { if (!open) setCloseOpen(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Close order {str(order?.order_number) ?? ""}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-fieldiq-text" data-testid="close-order-text">
            This ends the rental for every asset on this order today: open rental periods stop now, and all{" "}
            {displayValue(assets.length)} assets go back to Available. Return condition is not recorded here.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseOpen(false)} disabled={closeSubmitting}>Cancel</Button>
            <Button onClick={handleClose} disabled={closeSubmitting}>
              {closeSubmitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Close order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={linkOpen} onOpenChange={(o) => { if (!o) setLinkOpen(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send portal link for {str(order?.order_number) ?? ""}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-fieldiq-text" data-testid="send-portal-link-text">
            Emails the customer a link to view this order&apos;s equipment and invoices, without a login. The link works for
            30 days. Sending again to the same address replaces that person&apos;s link; other people&apos;s links keep working.
          </p>
          <div>
            <label htmlFor="send-portal-link-email" className="mb-1 block text-xs font-medium text-fieldiq-muted">Send to</label>
            <input
              id="send-portal-link-email"
              type="email"
              value={linkEmail}
              onChange={(e) => { setLinkEmail(e.target.value); setLinkRefusal(null); }}
              maxLength={255}
              placeholder="customer@company.com"
              className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-2 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
            />
          </div>
          {linkRefusal && (
            <p role="alert" data-testid="send-portal-link-refusal" className="text-sm font-medium text-fieldiq-text">{linkRefusal}</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkOpen(false)} disabled={linkSubmitting}>Close</Button>
            <Button onClick={handleSendLink} disabled={linkSubmitting || !linkEmail.trim()}>
              {linkSubmitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Send link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelOpen} onOpenChange={(o) => { if (!o) setCancelOpen(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel order {str(order?.order_number) ?? ""}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-fieldiq-text" data-testid="cancel-order-text">
            Cancels the order and every dispatch still to run on it, and releases the assets it reserved. The order is kept,
            marked Cancelled. An order that has had equipment go out, or has an invoice, can't be cancelled; close it instead.
          </p>
          <div>
            <label htmlFor="cancel-order-reason" className="mb-1 block text-xs font-medium text-fieldiq-muted">Reason (required)</label>
            <textarea
              id="cancel-order-reason"
              value={cancelReason}
              onChange={(e) => { setCancelReason(e.target.value.slice(0, 500)); setCancelRefusal(null); }}
              maxLength={500}
              rows={3}
              placeholder="Why is this order being cancelled?"
              className="w-full resize-none rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-2 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
            />
          </div>
          {cancelRefusal && (
            <p role="alert" data-testid="cancel-order-refusal" className="text-sm font-medium text-fieldiq-text">{cancelRefusal}</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)} disabled={cancelSubmitting}>Keep order</Button>
            <Button variant="destructive" onClick={handleCancel} disabled={cancelSubmitting || !cancelReason.trim()}>
              {cancelSubmitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Cancel order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
